import { AuthableRole, AuthableVertical } from '../types';

interface MenuTab {
  icon: string;
  title: string;
  prefix: string;
  path: string;
  target: null | '_blank';
  allowed: AuthableRole[];
  allowedIfInactive?: boolean;
  allowedIfInactiveRoles?: AuthableRole[];
  allowedIfSuspended?: boolean;
  allowedIfSuspendedRoles?: AuthableRole[];
  featureFlagName?: string;
  verticals?: AuthableVertical[];
}

export const CUSTOMER_PORTAL_ROLES: AuthableRole[] = ['admin', 'validator', 'branch-owner', 'branch-manager', 'market-manager'];
export const IDENTITY_ROLES: AuthableRole[] = ['investigator', 'validator', 'admin', 'self-service'];
export const REPORT_PORTAL_ROLES: AuthableRole[] = [
  'investigator',
  'validator',
  'admin',
  'branch-owner',
  'branch-manager',
  'market-manager',
  'self-service-investigator',
  'location-manager',
];
export const CASE_TRACKING_ROLES: AuthableRole[] = ['admin', 'branch-owner', 'branch-manager', 'market-manager', 'location-manager'];
export const GROCERY_CASE_TRACKING_ROLES: AuthableRole[] = [
  'admin',
  'branch-owner',
  'branch-manager',
  'market-manager',
  'location-manager',
];
export const UNCERTAIN_ROLES: AuthableRole[] = ['investigator', 'validator', 'admin', 'self-service'];
export const TRANSACTIONS_ROLES: AuthableRole[] = ['investigator', 'validator', 'admin', 'self-service-investigator'];
export const ACCESS_MANAGEMENT_ROLES: AuthableRole[] = ['admin', 'branch-owner', 'branch-manager', 'market-manager', 'location-manager'];
export const REVIEW_MANAGEMENT_ROLES: AuthableRole[] = ['admin', 'branch-owner', 'branch-manager', 'market-manager'];
export const INCIDENT_SUBMISSION_ROLES: AuthableRole[] = ['admin', 'self-service', 'self-service-investigator'];
export const MARKET_SNAPSHOT_ROLES: AuthableRole[] = ['admin', 'branch-owner', 'branch-manager', 'market-manager', 'market-health-manager'];
export const LP_PORTAL_ROLES: AuthableRole[] = ['admin', 'validator', 'investigator', 'self-service-investigator'];
export const API_KEY_ROLES: AuthableRole[] = ['admin'];
export const REPORT_DASHBOARD_ROLES: AuthableRole[] = ['admin', 'branch-owner', 'branch-manager', 'market-manager'];

export const LOCATION_GROUP_MANAGEMENT_ROLES: AuthableRole[] = ['admin'];
export const BRANCH_MANAGEMENT_GENERAL_ROLES: AuthableRole[] = [
  'admin',
  'branch-manager',
  'branch-owner',
  'market-manager',
  'installer',
  'self-service',
];
export const BRANCH_MANAGEMENT_SPECIFIC_ROLES: AuthableRole[] = [
  'admin',
  'branch-manager',
  'branch-owner',
  'market-manager',
  'installer',
  'self-service',
];
export const LOCATION_MANAGEMENT_GENERAL_ROLES: AuthableRole[] = [
  'admin',
  'branch-manager',
  'branch-owner',
  'market-manager',
  'installer',
  'self-service',
];
export const LOCATION_MANAGEMENT_SPECIFIC_ROLES: AuthableRole[] = [
  'admin',
  'branch-manager',
  'branch-owner',
  'market-manager',
  'market-health-manager',
  'installer',
  'self-service',
];
export const SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS = { LOCATION_ID: 'location_id' };

export const NAVIGATION_TABS: MenuTab[] = [
  {
    title: 'Case Tracking',
    icon: 'monitor',
    prefix: '/home',
    path: 'case-tracking',
    target: null,
    allowed: CASE_TRACKING_ROLES,
    verticals: ['MicroMarket'],
  },
  {
    title: 'Grocery Case Tracking',
    icon: 'grocery',
    prefix: '/home',
    path: 'grocery-case-portal',
    target: null,
    allowed: GROCERY_CASE_TRACKING_ROLES,
    verticals: ['Grocery'],
  },
  {
    title: 'Customer Portal',
    icon: 'home',
    prefix: '/home',
    path: 'customer-portal',
    target: null,
    allowed: CUSTOMER_PORTAL_ROLES,
    featureFlagName: 'enable_route_customer_portal',
    allowedIfInactive: true,
    allowedIfInactiveRoles: ['admin', 'branch-owner', 'branch-manager'],
  },
  {
    title: 'Review Management',
    icon: 'data_usage',
    prefix: '/home',
    path: 'review-management',
    target: null,
    allowed: REVIEW_MANAGEMENT_ROLES,
    featureFlagName: 'enable_usage_dashboard',
  },
  {
    title: 'Market Snapshots',
    icon: 'photo_camera',
    prefix: '/home',
    path: 'market-snapshots',
    featureFlagName: 'feature_flag_market_snapshots',
    target: null,
    allowed: MARKET_SNAPSHOT_ROLES,
  },
  {
    title: 'Report Dashboard',
    icon: 'breaking_news',
    prefix: '/home',
    path: 'reports',
    target: null,
    allowed: REPORT_DASHBOARD_ROLES,
  },
  {
    title: 'Report Portal',
    icon: 'analytics',
    prefix: '/home',
    path: 'report-portal',
    target: null,
    allowed: REPORT_PORTAL_ROLES,
  },
  {
    title: 'Uncertain',
    icon: 'face',
    prefix: '/home',
    path: 'uncertain',
    target: null,
    allowed: UNCERTAIN_ROLES,
    featureFlagName: 'enable_route_uncertain',
  },
  {
    title: 'Transactions',
    icon: 'receipt',
    prefix: '/home',
    path: 'transactions',
    target: null,
    allowed: TRANSACTIONS_ROLES,
  },
  {
    title: 'Access Management',
    icon: 'manage_accounts',
    prefix: '/home',
    path: 'user-management',
    target: null,
    allowed: ACCESS_MANAGEMENT_ROLES,
    allowedIfInactive: true,
  },
  {
    title: 'Branches & Locations',
    icon: 'security',
    prefix: '/home',
    path: 'manage',
    target: null,
    allowed: BRANCH_MANAGEMENT_GENERAL_ROLES,
    allowedIfSuspended: true,
    allowedIfSuspendedRoles: ['admin', 'branch-owner', 'branch-manager'],
  },
  {
    title: 'Incident Submission',
    icon: 'rate_review',
    prefix: '/home',
    path: 'incident-submission',
    target: null,
    allowed: INCIDENT_SUBMISSION_ROLES,
  },
  {
    title: 'API Keys',
    icon: 'vpn_key',
    prefix: '/home',
    path: 'apikey',
    target: null,
    allowed: API_KEY_ROLES,
  },
  { title: 'LP Portal', icon: 'open_in_new', prefix: '', path: 'lp', target: '_blank', allowed: LP_PORTAL_ROLES },
];

export const LP_NAVIGATION_TABS: MenuTab[] = [
  {
    title: 'Review Priorities',
    icon: 'format_list_numbered',
    prefix: '/lp/admin',
    path: 'priorities',
    target: null,
    allowed: ['admin'],
  },
  {
    title: 'Validations',
    icon: 'approval',
    prefix: '/lp',
    path: 'validate',
    target: null,
    allowed: ['admin', 'validator'],
  },
  {
    title: 'Upload Transactions',
    icon: 'upload_file',
    prefix: '/lp/admin',
    path: 'upload-transactions',
    target: null,
    allowed: ['admin'],
  },
  {
    title: 'Sideload Review',
    icon: 'post_add',
    prefix: '/lp/admin',
    path: 'sideload-review',
    target: null,
    allowed: ['admin'],
  },
  {
    title: 'Inspect Review',
    icon: 'zoom_in',
    prefix: '/lp/admin',
    path: 'inspect',
    target: null,
    allowed: ['admin', 'validator'],
  },
  {
    title: 'Location Instructions',
    icon: 'comment',
    prefix: '/lp/admin',
    path: 'location-instructions',
    target: null,
    allowed: ['admin', 'validator'],
  },
  {
    title: 'Investigation Queue',
    icon: 'playlist_add_check',
    prefix: '/lp/admin',
    path: 'investigation-queue',
    target: null,
    allowed: ['admin'],
  },
  {
    title: 'Manage Queue',
    icon: 'manage_search',
    prefix: '/lp/admin',
    path: 'manage-queue',
    target: null,
    allowed: ['admin'],
  },
];
